import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  TextField,
  Box,
  Stack,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Autocomplete,
  Popover,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  InputAdornment,
} from "@mui/material";
import {
  KeyboardArrowDown as DownIcon,
  KeyboardArrowUp as UpIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import {
  useTSQAuditList,
  useTSQRecordHistory,
  useTSQUserHistory,
} from "../../hooks/useTSAudits";
import { AuditTrailFull } from "../../types/auditTrailTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import stringUtils from "../../utils/stringUtils";

const AuditHistoryViewer: React.FC<{
  viewerType: "record" | "user" | "misc";
  tableName: string;
  recordId: string | null;
  fetch: boolean;
}> = ({ viewerType, tableName, recordId, fetch }) => {
  const [auditLogs, setAuditLogs] = useState<AuditTrailFull[]>([]);
  const [orderBy, setOrderBy] = useState<string>("timestamp");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const {
    data: recHistoryData,
    isLoading: recHistoryLoading,
    error: recHistoryError,
  } = useTSQRecordHistory(
    tableName,
    recordId || "",
    fetch && viewerType === "record"
  );

  const {
    data: userHistoryData,
    isLoading: userHistoryLoading,
    error: userHistoryError,
  } = useTSQUserHistory(recordId || "", fetch && viewerType === "record");

  const {
    data: miscData,
    isLoading: miscLoading,
    error: miscError,
  } = useTSQAuditList(
    {
      skip: -1,
      limit: -1,
      filters: [[]],
      active_status: "all",
    },
    fetch && viewerType === "misc"
  );

  useEffect(() => {
    if (recHistoryData && viewerType === "record") {
      setAuditLogs(recHistoryData);
      setTotalCount(recHistoryData.length);
    }
    if (userHistoryData && viewerType === "user") {
      setAuditLogs(userHistoryData);
      setTotalCount(userHistoryData.length);
    }
    if (miscData?.items && viewerType === "misc") {
      setAuditLogs(miscData.items);
      setTotalCount(miscData.items.length);
    }
  }, [recHistoryData, userHistoryData, miscData, viewerType]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (auditLog: AuditTrailFull, property: string): any => {
    switch (property) {
      case "operation_type":
        return auditLog.operation_type.toLowerCase();
      case "table_name":
        return auditLog.table_name?.toLowerCase() || "";
      case "timestamp":
        return new Date(auditLog.timestamp);
      case "audit_summary":
        return auditLog.audit_summary.toLowerCase();
      case "user":
        return auditLog.user
          ? `${auditLog.user?.first_name} ${auditLog.user?.last_name}`.toLowerCase()
          : "system";
      default:
        return "";
    }
  };

  const filteredAndSortedLogs = useMemo(() => {
    const filtered = auditLogs.filter((auditLog) => {
      const matchesSearch =
        searchTerm === "" ||
        auditLog.audit_summary
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        auditLog.operation_type
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (auditLog.table_name?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (auditLog.user
          ? `${auditLog.user?.first_name} ${auditLog.user?.last_name}`.toLowerCase()
          : "system"
        ).includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [auditLogs, searchTerm, orderBy, order, pagination]);

  const tableHeaders = useMemo(() => {
    switch (viewerType) {
      case "record":
        return ["user", "operation_type", "audit_summary", "timestamp"];
      case "user":
        return ["table_name", "operation_type", "audit_summary", "timestamp"];
      case "misc":
        return [
          "user",
          "table_name",
          "record_id",
          "operation_type",
          "audit_summary",
          "timestamp",
        ];
    }
  }, [viewerType]);

  const tableHeadMap: { [key: string]: string } = {
    user: "User",
    operation_type: "Operation",
    audit_summary: "Summary",
    timestamp: "Date/Time",
    table_name: "Record Type",
    record_id: "Record ID",
  };

  return (
    <Stack sx={{ direction: "column", gap: 1 }}>
      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder={`Search Logs by ${
            viewerType === "record"
              ? "User, Operation Type, or content in the Summary"
              : viewerType === "user"
              ? "Record Type, Operation Type, or content in the Summary"
              : "User, Record Type, Operation Type, or content in the Summary"
          }`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((v) => {
                return (
                  <TableCell sortDirection={orderBy === v ? order : false}>
                    <TableSortLabel
                      active={orderBy === v}
                      direction={orderBy === v ? order : "asc"}
                      onClick={() => handleSort(v)}
                    >
                      {tableHeadMap[v]}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!fetch && auditLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {searchTerm
                      ? "No Audit Logs found matching your search."
                      : "No Audit Logs found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedLogs.map((log: any) => (
                <TableRow key={log.id} hover>
                  {tableHeaders.map((v) => {
                    const value = (() => {
                      if (v === "user") {
                        if (log["user"]) {
                          return `${log["user"]["first_name"]} ${log["user"]["last_name"]}`;
                        } else {
                          return "System";
                        }
                      } else if (v === "timestamp") {
                        return stringUtils.formatDate(log[v]);
                      } else {
                        return log[v];
                      }
                    })();
                    return (
                      <TableCell>
                        <Typography variant="body1">{value}</Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Button
                      variant="contained"
                      onClick={() => console.log(log)}
                    >
                      Print Log Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Stack>
  );
};

export default AuditHistoryViewer;
